import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Nav } from '../nav/nav';
import { TopHeader } from '../top-header/top-header';
import { HouseListComponent } from './house-list/house-list.component';
import { FlatListComponent } from './flat-list/flat-list.component';
import { HouseSaveEvent, NewEditHouseComponent } from './house-list/new-edit-house/new-edit-house.component';
import { NewEditFlatComponent } from './flat-list/new-edit-flat/new-edit-flat.component';
import { FilterField, FilterModalComponent } from '../core/ui/filter-modal/filter-modal.component';
import { TranslatePipe } from '../core/pipes/translate.pipe';

type HouseTab = 'House' | 'Flat';

@Component({
  selector: 'app-house',
  standalone: true,
  imports: [
    CommonModule,
    MatSnackBarModule,
    Nav,
    TopHeader,
    HouseListComponent,
    FlatListComponent,
    NewEditHouseComponent,
    NewEditFlatComponent,
    FilterModalComponent,
    TranslatePipe
  ],
  templateUrl: './house.html',
  styleUrls: [
    './house.css',
    './../../CSSFiles/Tabs.css',
    './../../CSSFiles/BTN_Style.css'
  ],
})
export class HouseComponent {
  @ViewChild(HouseListComponent) houseList?: HouseListComponent;
  @ViewChild(FlatListComponent) flatList?: FlatListComponent;
  activeTab: HouseTab = 'House';
  showNewHouse = false;
  showNewFlat = false;
  showPostCreateFlatPrompt = false;
  showFilter = false;
  houseFilterValues: Record<string, any> = {};
  flatFilterValues: Record<string, any> = {};
  houseFilterActive = false;
  flatFilterActive = false;
  pendingCreatedHouseForFlat: { id: number | null; houseName: string } | null = null;
  flatCreateHousePreset: { id: number | null; houseName: string } | null = null;

  constructor(private readonly snackBar: MatSnackBar) {}

  houseFilterFields: FilterField[] = [
    {
      key: 'houseName',
      label: 'filter.houseName',
      type: 'text',
      placeholder: 'placeholder.typeHouseName'
    },
    {
      key: 'houseAddress',
      label: 'filter.address',
      type: 'text',
      placeholder: 'placeholder.typeAddress'
    },
    {
      key: '__showDeleted',
      label: 'common.data',
      type: 'switch',
      switchLabel: 'common.showDeletedData'
    }
  ];

  flatFilterFields: FilterField[] = [
    {
      key: 'flatName',
      label: 'filter.flatName',
      type: 'text',
      placeholder: 'placeholder.typeFlatName'
    },
    {
      key: 'house.id',
      label: 'filter.house',
      type: 'select',
      placeholder: 'placeholder.selectHouse',
      dropdownObj: {
        apiInfo: 'HOUSE_LIST',
        name: 'house.id',
        valueKey: 'id',
        display1: 'houseName',
        display2: 'houseNo',
        label: 'filter.house',
        placeholder: 'placeholder.selectHouse',
        isSearchable: true
      }
    },
    {
      key: 'floorName',
      label: 'filter.floor',
      type: 'select',
      placeholder: 'placeholder.selectFloor',
      dependsOnKey: 'house.id',
      dependsOnOptionsKey: 'floors',
      dropdownObj: {
        name: 'floorName',
        valueKey: 'value',
        display1: 'label',
        label: 'filter.floor',
        placeholder: 'placeholder.selectFloor',
        options: [],
        isSearchable: true,
        searchReadonly: true
      }
    },
    {
      key: '__showDeleted',
      label: 'common.data',
      type: 'switch',
      switchLabel: 'common.showDeletedData'
    }
  ];

  selectTab(tab: HouseTab): void {
    if (this.activeTab !== tab) {
      if (tab === 'House') {
        this.houseFilterValues = { __showDeleted: false };
        this.houseFilterActive = false;
        this.houseList?.setFilters({ status: 1 });
      } else {
        this.flatFilterValues = { __showDeleted: false };
        this.flatFilterActive = false;
        this.flatList?.setFilters({ status: 1 });
      }
    }
    this.activeTab = tab;
    this.showFilter = false;
  }

  openNewHouse(): void {
    this.showNewHouse = true;
  }

  closeNewHouse(): void {
    this.showNewHouse = false;
  }

  handleHouseSave(event?: HouseSaveEvent): void {
    this.showNewHouse = false;
    this.houseList?.fetchHouses();
    if (event?.mode === 'create') {
      this.pendingCreatedHouseForFlat = {
        id: event.refId ?? null,
        houseName: event.houseName
      };
      this.showPostCreateFlatPrompt = true;
    }
  }

  openNewFlat(): void {
    if (this.flatList?.isCreateDisabled) {
      this.openLimitToast(this.flatList.showCreateLimitMessage());
      return;
    }
    this.flatCreateHousePreset = null;
    this.showNewFlat = true;
  }

  closeNewFlat(): void {
    this.showNewFlat = false;
    this.flatCreateHousePreset = null;
  }

  handleFlatSave(): void {
    this.showNewFlat = false;
    this.flatCreateHousePreset = null;
    this.flatList?.fetchFlats();
  }

  closePostCreateFlatPrompt(): void {
    this.showPostCreateFlatPrompt = false;
    this.pendingCreatedHouseForFlat = null;
  }

  openFlatCreateFromHousePrompt(): void {
    if (this.flatList?.isCreateDisabled) {
      this.openLimitToast(this.flatList.showCreateLimitMessage());
      return;
    }

    this.activeTab = 'Flat';
    this.showFilter = false;
    this.flatCreateHousePreset = this.pendingCreatedHouseForFlat
      ? { ...this.pendingCreatedHouseForFlat }
      : null;
    this.showPostCreateFlatPrompt = false;
    this.pendingCreatedHouseForFlat = null;
    this.showNewFlat = true;
  }

  toggleFilter(): void {
    this.showFilter = !this.showFilter;
  }

  isFilterActive(): boolean {
    return this.activeTab === 'House' ? this.houseFilterActive : this.flatFilterActive;
  }

  applyFilters(values: Record<string, any>, isActive = false): void {
    this.showFilter = false;
    if (this.activeTab === 'House') {
      const normalized = this.normalizeFilters(values);
      this.houseFilterValues = { ...values };
      this.houseFilterActive = isActive;
      this.houseList?.setFilters(normalized);
      return;
    }

    const normalized = this.normalizeFilters(values);
    this.flatFilterValues = { ...values };
    this.flatFilterActive = isActive;
    this.flatList?.setFilters(normalized);
  }

  resetFilters(): void {
    if (this.activeTab === 'House') {
      const defaultFilters = { status: 1 };
      this.houseFilterValues = { __showDeleted: false };
      this.houseFilterActive = false;
      this.houseList?.setFilters(defaultFilters);
      return;
    }

    const defaultFilters = { status: 1 };
    this.flatFilterValues = { __showDeleted: false };
    this.flatFilterActive = false;
    this.flatList?.setFilters(defaultFilters);
  }

  private normalizeFilters(values: Record<string, any>): Record<string, any> {
    const showDeleted = !!values?.['__showDeleted'];
    const { __showDeleted, ...rest } = values || {};
    return {
      ...rest,
      status: showDeleted ? 0 : 1
    };
  }

  private openLimitToast(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3500,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['dashboard-toast-warning']
    });
  }
}
